/**
 * Created: 2024-03-27
 * Last Modified: 2024-08-10
 * Modified by: Added 'use client' directive and runtime edge export
 * Updated: 2024-12-XX - Modified to show "Wrong Door" error page instead of redirecting to login
 *
 * Root Page Component
 * Shows "Wrong Door" error page when accessed directly without OAuth parameters
 */

'use client'

import {useEffect, useState} from 'react'
import {useSearchParams} from 'next/navigation'
import {useRouter} from 'next/navigation'
import {Box, Heading, VStack, Divider, Text, Alert, AlertIcon, Button, Link, HStack, Spinner} from '@chakra-ui/react'

export default function RootPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted) return

    const clientId = searchParams.get('client_id')
    const redirectUri = searchParams.get('redirect_uri')
    const state = searchParams.get('state')
    const responseType = searchParams.get('response_type')

    // If this looks like a valid OAuth flow, redirect to login
    if (redirectUri || clientId || state || responseType) {
      router.push('/login')
    }
  }, [router, searchParams, mounted])

  // Show loading spinner while checking

  const clientId = searchParams.get('client_id')
  const redirectUri = searchParams.get('redirect_uri')
  const state = searchParams.get('state')
  const responseType = searchParams.get('response_type')

  // If this looks like a valid OAuth flow, don't show the error page (let redirect happen)
  if (redirectUri || clientId || state || responseType || !mounted) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" h="full">
        <Spinner size="xl" color="blue.500" />
      </Box>
    )
  }

  // Show "Wrong Door" error page for direct access
  return (
    <VStack spacing={6} align="stretch">
      <Box mb={2}>
        <Alert status="warning" borderRadius="md" bg="orange.50" border="1px solid" borderColor="orange.200">
          <AlertIcon color="orange.500" />
          <Text color="orange.800" fontWeight="medium">
            Access Error
          </Text>
        </Alert>
      </Box>

      <Box textAlign="center">
        <Heading as="h1" size="lg" color="gray.800" fontWeight="semibold" mb={4}>
          You've reached the authentication service directly
        </Heading>
        <Text color="gray.600" fontSize="md" mb={6}>
          This service handles authentication for FRNT applications and is not intended for direct access.
        </Text>
        <Text color="gray.700" fontSize="md" fontWeight="medium" mb={4}>
          To sign in, please visit your application directly:
        </Text>
      </Box>

      <VStack spacing={3} align="stretch">
        <Link href={typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://console.frnt.io'} isExternal>
          <Button
            w="full"
            variant="outline"
            colorScheme="primary"
            size="lg"
            leftIcon={
              <span role="img" aria-label="Office building" style={{fontSize: '1.125rem'}}>
                🏢
              </span>
            }>
            <HStack justify="space-between" w="full">
              <Text>FRNT Console</Text>
              <Text fontSize="sm" color="gray.500">
                For administrators
              </Text>
            </HStack>
          </Button>
        </Link>

        <Link href={typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:5001' : 'https://trade.frnt.io'} isExternal>
          <Button
            w="full"
            variant="outline"
            colorScheme="primary"
            size="lg"
            leftIcon={
              <span role="img" aria-label="Chart" style={{fontSize: '1.125rem'}}>
                📊
              </span>
            }>
            <HStack justify="space-between" w="full">
              <Text>FRNT Trade</Text>
              <Text fontSize="sm" color="gray.500">
                For trading platform
              </Text>
            </HStack>
          </Button>
        </Link>

        <Link href={typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:4200' : 'https://market.frnt.io'} isExternal>
          <Button
            w="full"
            variant="outline"
            colorScheme="primary"
            size="lg"
            leftIcon={
              <span role="img" aria-label="Chart with upwards trend" style={{fontSize: '1.125rem'}}>
                📈
              </span>
            }>
            <HStack justify="space-between" w="full">
              <Text>FRNT Market Data</Text>
              <Text fontSize="sm" color="gray.500">
                For market insights
              </Text>
            </HStack>
          </Button>
        </Link>
      </VStack>

      <Divider />

      <Box textAlign="center">
        <Text fontSize="sm" color="gray.500">
          Having trouble? Contact your administrator or{' '}
          <Link color="blue.500" href="mailto:support@frnt.io">
            support@frnt.io
          </Link>
        </Text>
      </Box>
    </VStack>
  )
}
